import React from 'react';
import { 
  Info, 
  Percent, 
  Calculator, 
  BookOpen, 
  PieChart, 
  TrendingUp, 
  AlertTriangle, 
  Target 
} from 'lucide-react';

const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-green-500" />
        Проценты: полное руководство
      </h2>
      
      <div className="prose prose-green dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-green-500" />
          Что такое проценты?
        </h3>
        <p>
          <strong>Проценты</strong> — это способ выражения числа как части из ста. 
          Слово «процент» происходит от латинского <em>per centum</em>, что означает 
          «из ста». Проценты широко используются для описания долей, изменений и соотношений 
          в различных областях: финансах, статистике, науке и повседневной жизни.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-green-500" />
          Формы представления процентов
        </h3>
        <p>Существует несколько основных способов представления процентов:</p>
        <ul>
          <li>
            <strong>Проценты (%)</strong>: Стандартное представление, где 100% означает целое, 
            50% — половину, 25% — четверть.
          </li>
          <li>
            <strong>Десятичная дробь</strong>: Число от 0 до 1, где 1 = 100%, 0.5 = 50%, 
            0.25 = 25%. Удобно для математических расчетов.
          </li>
          <li>
            <strong>Обыкновенная дробь</strong>: Например, 1/4 = 25%, 1/2 = 50%. 
            Используется в математике и при простых вычислениях.
          </li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-4">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <Target className="w-4 h-4" />
            Простые преобразования
          </h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm">
            • 100% = 1 (десятичная дробь)
            • 50% = 0.5 (десятичная дробь)
            • 25% = 1/4 (обыкновенная дробь)
            • 10% = 0.1 (десятичная дробь)
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          Применение процентов в различных областях
        </h3>
        <ul>
          <li>
            <strong>Финансы</strong>: Проценты используются в инвестициях, 
            кредитовании, депозитах для расчета доходности и процентных ставок.
          </li>
          <li>
            <strong>Статистика</strong>: Для представления долей, изменений 
            и соотношений в исследованиях и аналитике.
          </li>
          <li>
            <strong>Экономика</strong>: Расчет инфляции, рентабельности, 
            доли рынка и других экономических показателей.
          </li>
          <li>
            <strong>Наука</strong>: Представление концентраций, составов 
            и других научных измерений.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-green-500" />
          Как выполнять расчеты с процентами
        </h3>
        <p>Основные операции с процентами:</p>
        <ul>
          <li>
            <strong>Перевод процентов в десятичную дробь</strong>: 
            Разделите число на 100 (например, 25% = 25 ÷ 100 = 0.25)
          </li>
          <li>
            <strong>Перевод десятичной дроби в проценты</strong>: 
            Умножьте число на 100 (например, 0.25 = 0.25 × 100 = 25%)
          </li>
          <li>
            <strong>Расчет процента от числа</strong>: 
            Умножьте число на процент в десятичной форме
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-4">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Распространенные ошибки
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm list-disc pl-4">
            <li>Путать проценты и десятичные дроби</li>
            <li>Неправильно выполнять математические операции</li>
            <li>Забывать о переводе между различными формами представления</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <PieChart className="w-5 h-5 text-green-500" />
          Практические советы
        </h3>
        <ul>
          <li>Всегда проверяйте контекст использования процентов</li>
          <li>Используйте калькулятор для точных вычислений</li>
          <li>Практикуйтесь в быстром mentally переводе процентов</li>
          <li>Понимайте логику представления процентов в разных формах</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш конвертер процентов поможет вам легко преобразовывать 
          значения между различными формами представления!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;